/**
 * CardCaptureDialog · 2026-10-25
 *
 * In-app camera for business cards — same pattern as BarcodeScannerDialog but
 * tuned for landscape 2:1 cards. On shutter press we crop the viewfinder
 * rectangle out of the live video frame and POST it to
 * ``/api/ai/scan-business-card``. The returned ``{name, contact_person, email,
 * phone, address, title, website}`` is handed back to the caller which fills
 * the 新增客户 dialog without any typing.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, X, CreditCard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export const CardCaptureDialog = ({ open, onOpenChange, onDetect }) => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const videoFrameRef = useRef(null);
  const cropFrameRef = useRef(null);
  const streamRef = useRef(null);
  const [starting, setStarting] = useState(false);
  const [camStarted, setCamStarted] = useState(false);
  const [busy, setBusy] = useState(false);

  const stopCamera = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCamStarted(false);
  }, []);

  // Start the preview camera when the dialog opens, stop on close.
  useEffect(() => {
    if (!open) { stopCamera(); return undefined; }
    let cancelled = false;
    const start = async () => {
      setStarting(true);
      await new Promise((r) => setTimeout(r, 150));
      if (cancelled) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute("playsinline", "true");
          try { await videoRef.current.play(); } catch (_e) { /* iOS autoplay */ }
        }
        setCamStarted(true);
      } catch (e) {
        if (!cancelled) {
          const name = e?.name || "";
          const msg = name === "NotAllowedError"
            ? (t("scanner.permDenied") || "请在浏览器设置里允许访问摄像头")
            : name === "NotFoundError"
              ? (t("scanner.noCamera") || "未检测到可用摄像头")
              : (t("scanner.cameraDenied") || "无法启动摄像头") + ` (${name || e.message || "unknown"})`;
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    };
    start();
    return () => { cancelled = true; stopCamera(); };
  }, [open, stopCamera, t]);

  // Capture, crop to viewfinder, upload to /ai/scan-business-card.
  const capture = async () => {
    const video = videoRef.current;
    const container = videoFrameRef.current;
    const frame = cropFrameRef.current;
    if (!video || !container || !frame || !video.videoWidth) {
      toast.error(t("scanner.cameraDenied") || "摄像头未就绪");
      return;
    }
    setBusy(true);
    const t0 = performance.now();
    try {
      const cBox = container.getBoundingClientRect();
      const fBox = frame.getBoundingClientRect();
      const relX = (fBox.left - cBox.left) / cBox.width;
      const relY = (fBox.top - cBox.top) / cBox.height;
      const relW = fBox.width / cBox.width;
      const relH = fBox.height / cBox.height;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const containerAspect = cBox.width / cBox.height;
      const videoAspect = vw / vh;
      let drawW, drawH, drawX, drawY;
      if (videoAspect > containerAspect) {
        drawH = vh; drawW = vh * containerAspect;
        drawX = (vw - drawW) / 2; drawY = 0;
      } else {
        drawW = vw; drawH = vw / containerAspect;
        drawX = 0; drawY = (vh - drawH) / 2;
      }
      const sx = drawX + relX * drawW;
      const sy = drawY + relY * drawH;
      const sw = relW * drawW;
      const sh = relH * drawH;

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("canvas.toBlob failed");
      console.log(`[card-scan] crop ${canvas.width}x${canvas.height} = ${blob.size}B in ${(performance.now() - t0).toFixed(0)}ms`);

      const t1 = performance.now();
      const form = new FormData();
      form.append("file", new File([blob], "card.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/ai/scan-business-card", form, { timeout: 60000 });
      console.log(`[card-scan] upload+ocr ${(performance.now() - t1).toFixed(0)}ms, total ${(performance.now() - t0).toFixed(0)}ms`);
      const anyField = data && (data.name || data.contact_person || data.email || data.phone || data.address);
      if (anyField) {
        toast.success(t("card.recognized") || "名片识别成功");
        onDetect?.(data);
        stopCamera();
        onOpenChange?.(false);
      } else {
        toast.warning(t("card.notRecognized") || "没有识别到任何信息，请对准名片重拍");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || (t("scanner.ocrFailed") || "OCR 失败"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopCamera(); onOpenChange?.(o); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="card-capture-dialog">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            <CreditCard size={16} className="text-brand" /> {t("card.title") || "拍照识别名片"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t("card.hint") || "把名片放进方框，按下方圆钮拍摄，AI 自动填入客户信息"}
          </DialogDescription>
        </DialogHeader>

        <div className="relative bg-black" data-testid="card-ocr-wrapper">
          <div ref={videoFrameRef} className="relative w-full aspect-[4/3] overflow-hidden">
            <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="card-ocr-video" />
            {(starting || !camStarted) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/90 text-xs gap-2 bg-black/40">
                <Loader2 size={28} className="animate-spin" />
                <span>{t("scanner.starting") || "摄像头启动中…"}</span>
              </div>
            )}
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
              {/* 名片一般是 ~1.75:1 的横版 */}
              <div
                ref={cropFrameRef}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[86%] aspect-[1.75/1] rounded-md border-2 border-brand"
                data-testid="card-ocr-crop-frame"
                style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }}
              >
                <span className="absolute -top-0.5 -left-0.5 w-5 h-5 border-l-2 border-t-2 border-white" />
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 border-r-2 border-t-2 border-white" />
                <span className="absolute -bottom-0.5 -left-0.5 w-5 h-5 border-l-2 border-b-2 border-white" />
                <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 border-r-2 border-b-2 border-white" />
              </div>
              <div className="absolute left-0 right-0 bottom-3 text-center text-white/90 text-[11px] font-medium">
                {t("card.alignHint") || "把名片对准方框内，按下方圆钮拍摄"}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center py-3 bg-black">
            <button
              type="button"
              onClick={capture}
              disabled={!camStarted || busy}
              className="w-16 h-16 rounded-full bg-white border-4 border-white/70 shadow-lg flex items-center justify-center active:scale-95 transition disabled:opacity-50"
              data-testid="card-ocr-capture"
              aria-label={t("scanner.capture") || "拍摄"}
            >
              {busy ? <Loader2 size={28} className="animate-spin text-brand" /> : <div className="w-12 h-12 rounded-full bg-brand" />}
            </button>
          </div>
          <button
            type="button"
            onClick={() => { stopCamera(); onOpenChange?.(false); }}
            className="absolute top-2 right-2 h-9 w-9 rounded-full bg-black/50 text-white flex items-center justify-center"
            data-testid="card-ocr-close"
          >
            <X size={16} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
